// frontend/src/app/admin/ingress/AdminIngressStarterAppsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiJson, ApiError } from "../../../lib/api";
import { getTenantUuid } from "../../ingress/tenant";

const TENANT_UUID = getTenantUuid();

type Blueprint = {
  key: string;
  slug: string;
  name: string;
  category: string;
  description: string;
  requires_providers: number;
  inputs: { required: string[]; properties: Record<string, any> };
  ui_fields: Array<{ key: string; label?: string; widget?: string; placeholder?: string }>;
  flow_spec: any;
  output_rules: any;
};

type IngressAppRow = {
  id: string;
  slug: string;
  name: string;
  is_published: boolean;
  flow_id: string;
  version_id: string;
};

type Preflight = {
  ok: boolean;
  tenant_id: string;
  tenant_label: string;
  key: string;
  slug: string;
  publish_intent: boolean;
  reinstall_intent: boolean;
  blueprint_summary: {
    name?: string;
    slug?: string;
    category?: string;
    description?: string;
    requires_providers?: number;
  };
  requirements: {
    llm_nodes: Array<{
      node_id: string;
      model: string;
      routing: {
        mode: "single" | "fan_out";
        strategy?: string | null;
        fan_in?: string | null;
        allowed_providers: string[];
        fallback_chain: string[];
        rules: any[];
        max_providers?: number | null;
      };
    }>;
    required_models: string[];
    guardrails?: any;
  };
  provider_matches: Array<{
    model: string;
    status: "ok" | "warn" | "blocked";
    candidates: Array<{
      id: string;
      name: string;
      provider_type: string;
      model_name: string;
      is_active: boolean;
      health?: { healthy: boolean; reason: string; checked_at: number };
    }>;
    blocked_reasons: string[];
    warnings: string[];
  }>;
  publish_ready: boolean;
  warnings: string[];
  errors: string[];
};

function pretty(v: any) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function normalizeApps(payload: any): IngressAppRow[] {
  if (Array.isArray(payload)) return payload as any;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.items)) return payload.items as any;
    if (Array.isArray(payload.apps)) return payload.apps as any;
  }
  return [];
}

export default function AdminIngressStarterAppsPage() {
  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [apps, setApps] = useState<IngressAppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // modal state
  const [pfOpen, setPfOpen] = useState(false);
  const [pf, setPf] = useState<Preflight | null>(null);
  const [pfBusy, setPfBusy] = useState(false);
  const [pfErr, setPfErr] = useState<string | null>(null);
  const [pfIntent, setPfIntent] = useState<{ key: string; publish: boolean; reinstall: boolean } | null>(null);

  async function loadAll() {
    setLoading(true);
    setErr(null);
    setMsg(null);
    try {
      const bps = await apiJson<Blueprint[]>(
        `/v1/admin/ingress/starter_apps/blueprints?tenant_id=${TENANT_UUID}`
      );
      setBlueprints(bps);

      const a = await apiJson<any>(`/v1/admin/ingress/apps?tenant_id=${TENANT_UUID}`);
      setApps(normalizeApps(a));
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setApps([]);
      setBlueprints([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const bySlug = useMemo(() => {
    const m = new Map<string, IngressAppRow>();
    for (const a of apps) m.set(a.slug, a);
    return m;
  }, [apps]);

  async function openPreflight(b: Blueprint, publish: boolean, reinstall: boolean) {
    setPfOpen(true);
    setPf(null);
    setPfErr(null);
    setPfBusy(true);
    setPfIntent({ key: b.key, publish, reinstall });

    try {
      const q = new URLSearchParams({
        tenant_id: TENANT_UUID,
        key: b.key,
        publish: publish ? "true" : "false",
        reinstall: reinstall ? "true" : "false",
      }).toString();

      const data = await apiJson<Preflight>(`/v1/admin/ingress/starter_apps/preflight?${q}`);
      setPf(data);
    } catch (e: any) {
      setPfErr(e instanceof ApiError ? e.message : (e?.message || String(e)));
    } finally {
      setPfBusy(false);
    }
  }

  async function confirmInstall({ forceUnpublished = false }: { forceUnpublished?: boolean }) {
    if (!pfIntent) return;
    setPfErr(null);
    setPfBusy(true);
    try {
      // If publish intent is blocked, allow user to install unpublished (publish=false)
      const publish = forceUnpublished ? false : pfIntent.publish;

      const q = new URLSearchParams({
        tenant_id: TENANT_UUID,
        key: pfIntent.key,
        publish: publish ? "true" : "false",
        reinstall: pfIntent.reinstall ? "true" : "false",
      }).toString();

      const resp = await apiJson<any>(`/v1/admin/ingress/starter_apps/install?${q}`, { method: "POST" });
      setMsg(
        `${resp?.action ? `${resp.action}: ` : ""}Installed ${resp?.slug || pfIntent.key} (${publish ? "published" : "unpublished"})`
      );
      setPfOpen(false);
      await loadAll();
    } catch (e: any) {
      // backend returns 409 with detail.preflight sometimes; show friendly text
      const text = e instanceof ApiError ? e.message : (e?.message || String(e));
      setPfErr(text);
    } finally {
      setPfBusy(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-600">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Starter Apps</h2>
          <div className="text-sm text-gray-600 mt-1">
            Install demo-ready app templates (flows + schemas) into your tenant.
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Tenant UUID: <code>{TENANT_UUID}</code>
          </div>
        </div>
        <button className="px-3 py-2 rounded border text-sm hover:bg-gray-50" onClick={loadAll}>
          Refresh
        </button>
      </div>

      {msg && <div className="p-3 rounded border border-green-300 bg-green-50 text-sm text-green-800">{msg}</div>}
      {err && <div className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800">{err}</div>}

      {/* Preflight modal */}
      {pfOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg border">
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <div className="text-lg font-semibold">Preflight: Starter App Install</div>
                <div className="text-xs text-gray-500 mt-1">
                  Validates providers and routing readiness for this tenant before install/publish.
                </div>
              </div>
              <button
                className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                onClick={() => { if (!pfBusy) setPfOpen(false); }}
                disabled={pfBusy}
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-auto">
              {pfBusy && <div className="text-sm text-gray-600">Running preflight…</div>}
              {pfErr && <div className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800">{pfErr}</div>}

              {pf && (
                <>
                  {/* 1) Blueprint summary */}
                  <div className="rounded border p-3">
                    <div className="text-sm font-semibold">Blueprint</div>
                    <div className="text-sm text-gray-700 mt-1">
                      <div><span className="font-medium">Name:</span> {pf.blueprint_summary?.name}</div>
                      <div><span className="font-medium">Slug:</span> <code>{pf.slug}</code></div>
                      <div><span className="font-medium">Category:</span> {pf.blueprint_summary?.category}</div>
                      <div className="mt-1"><span className="font-medium">Description:</span> {pf.blueprint_summary?.description}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        requires_providers: {pf.blueprint_summary?.requires_providers ?? 0}
                      </div>
                    </div>
                  </div>

                  {/* 2) Tenant context */}
                  <div className="rounded border p-3">
                    <div className="text-sm font-semibold">Tenant Context</div>
                    <div className="text-sm text-gray-700 mt-1">
                      <div><span className="font-medium">tenant_label:</span> <code>{pf.tenant_label}</code></div>
                      <div><span className="font-medium">tenant_uuid:</span> <code>{pf.tenant_id}</code></div>
                    </div>
                  </div>

                  {/* 3) Flow requirements */}
                  <div className="rounded border p-3">
                    <div className="text-sm font-semibold">Flow Requirements</div>
                    {pf.requirements?.llm_nodes?.length === 0 ? (
                      <div className="text-sm text-gray-700 mt-1">No LLM nodes detected (no providers required).</div>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {pf.requirements.llm_nodes.map((n, i) => (
                          <div key={i} className="rounded border bg-gray-50 p-2">
                            <div className="text-sm">
                              <span className="font-medium">node_id:</span> <code>{n.node_id}</code>{" "}
                              <span className="font-medium ml-2">model:</span> <code>{n.model}</code>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              routing: mode=<code>{n.routing.mode}</code>
                              {n.routing.strategy ? <> strategy=<code>{n.routing.strategy}</code></> : null}
                              {n.routing.fan_in ? <> fan_in=<code>{n.routing.fan_in}</code></> : null}
                              {typeof n.routing.max_providers === "number" ? <> max_providers=<code>{n.routing.max_providers}</code></> : null}
                            </div>
                            {(n.routing.allowed_providers?.length || n.routing.fallback_chain?.length) ? (
                              <div className="text-xs text-gray-600 mt-1">
                                {n.routing.allowed_providers?.length ? (
                                  <div>allowed_providers: <code>{n.routing.allowed_providers.join(", ")}</code></div>
                                ) : null}
                                {n.routing.fallback_chain?.length ? (
                                  <div>fallback_chain: <code>{n.routing.fallback_chain.join(", ")}</code></div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 mt-1">No provider name restrictions in routing.</div>
                            )}
                            {n.routing.rules?.length ? (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-xs text-gray-700">rules ({n.routing.rules.length})</summary>
                                <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-48 mt-2">
                                  {pretty(n.routing.rules)}
                                </pre>
                              </details>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 4) Provider match results */}
                  <div className="rounded border p-3">
                    <div className="text-sm font-semibold">Provider Matches</div>
                    {pf.provider_matches?.length === 0 ? (
                      <div className="text-sm text-gray-700 mt-1">No provider checks required.</div>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {pf.provider_matches.map((m, i) => (
                          <div key={i} className="rounded border p-2">
                            <div className="flex items-center justify-between">
                              <div className="text-sm">
                                model: <code>{m.model}</code>
                              </div>
                              <div className="text-xs">
                                {m.status === "ok" && <span className="px-2 py-1 rounded bg-green-100 text-green-800">ok</span>}
                                {m.status === "warn" && <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800">warn</span>}
                                {m.status === "blocked" && <span className="px-2 py-1 rounded bg-red-100 text-red-800">blocked</span>}
                              </div>
                            </div>

                            {m.blocked_reasons?.length ? (
                              <div className="mt-2 text-sm text-red-700">
                                <div className="font-medium">Blocked reasons</div>
                                <ul className="list-disc ml-5">
                                  {m.blocked_reasons.map((r, idx) => <li key={idx}>{r}</li>)}
                                </ul>
                              </div>
                            ) : null}

                            {m.warnings?.length ? (
                              <div className="mt-2 text-sm text-yellow-800">
                                <div className="font-medium">Warnings</div>
                                <ul className="list-disc ml-5">
                                  {m.warnings.map((r, idx) => <li key={idx}>{r}</li>)}
                                </ul>
                              </div>
                            ) : null}

                            <div className="mt-2">
                              <div className="text-xs text-gray-500">Active providers for this model:</div>
                              {m.candidates?.length ? (
                                <div className="mt-1 space-y-1">
                                  {m.candidates.map((c) => (
                                    <div key={c.id} className="text-xs border rounded p-2 bg-gray-50">
                                      <div>
                                        <span className="font-medium">{c.name}</span>{" "}
                                        <span className="text-gray-500">({c.provider_type} · {c.model_name})</span>
                                      </div>
                                      <div className="text-gray-600">
                                        active: <code>{String(c.is_active)}</code>{" "}
                                        {c.health ? (
                                          <>
                                            · health:{" "}
                                            <code>{String(c.health.healthy)}</code>{" "}
                                            <span className="text-gray-500">{c.health.reason}</span>
                                          </>
                                        ) : null}
                                      </div>
                                      <div className="text-gray-500 font-mono">{c.id}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 mt-1">None</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 5) Publish readiness */}
                  <div className="rounded border p-3">
                    <div className="text-sm font-semibold">Publish Readiness</div>
                    <div className="mt-2">
                      {pf.publish_ready ? (
                        <div className="p-2 rounded bg-green-50 border border-green-200 text-green-800 text-sm">
                          ✅ publish-ready
                        </div>
                      ) : (
                        <div className="p-2 rounded bg-red-50 border border-red-200 text-red-800 text-sm">
                          ❌ publish blocked — provider requirements not met
                        </div>
                      )}
                    </div>

                    {(pf.errors?.length || pf.warnings?.length) ? (
                      <div className="mt-2 text-sm">
                        {pf.errors?.length ? (
                          <div className="text-red-700">
                            <div className="font-medium">Errors</div>
                            <ul className="list-disc ml-5">
                              {pf.errors.map((e, idx) => <li key={idx}>{e}</li>)}
                            </ul>
                          </div>
                        ) : null}

                        {pf.warnings?.length ? (
                          <div className="text-yellow-800 mt-2">
                            <div className="font-medium">Warnings</div>
                            <ul className="list-disc ml-5">
                              {pf.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* 6) Actions */}
            <div className="p-4 border-t flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500">
                {pfIntent ? (
                  <>
                    intent: key=<code>{pfIntent.key}</code> publish=<code>{String(pfIntent.publish)}</code>{" "}
                    reinstall=<code>{String(pfIntent.reinstall)}</code>
                  </>
                ) : null}
              </div>

              <div className="flex gap-2">
                <button
                  className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                  onClick={() => setPfOpen(false)}
                  disabled={pfBusy}
                >
                  Cancel
                </button>

                {/* If publish intent and blocked, allow install unpublished */}
                {pf && pfIntent?.publish && !pf.publish_ready ? (
                  <button
                    className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                    onClick={() => confirmInstall({ forceUnpublished: true })}
                    disabled={pfBusy}
                    title="Installs the app but does not publish it"
                  >
                    Install Unpublished
                  </button>
                ) : null}

                {/* Confirm publish if ready or confirm install if publish=false */}
                <button
                  className="px-3 py-2 rounded bg-black text-white text-sm disabled:opacity-60"
                  onClick={() => confirmInstall({ forceUnpublished: false })}
                  disabled={pfBusy || !pf || (pfIntent?.publish && !pf.publish_ready)}
                  title={pfIntent?.publish ? "Confirm install & publish" : "Confirm install"}
                >
                  {pfBusy ? "Working…" : pfIntent?.publish ? "Confirm Publish" : "Confirm Install"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {blueprints.length === 0 ? (
        <div className="p-4 border rounded bg-white">
          <div className="text-sm text-gray-700">Blueprint repository not available.</div>
          <div className="text-xs text-gray-500 mt-1">
            Ensure backend router is mounted: <code>app.routers.admin.starter_apps</code>.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {blueprints.map((b) => {
            const installed = bySlug.has(b.slug);
            const row = bySlug.get(b.slug);

            return (
              <div key={b.slug} className="border rounded bg-white p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-gray-500">{b.category}</div>
                    <div className="text-lg font-semibold">{b.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{b.slug}</div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-60"
                      onClick={() => openPreflight(b, false, false)}
                      disabled={pfBusy}
                      title={installed ? "Install (unpublished) - will 409 if already installed" : "Install (unpublished)"}
                    >
                      Install
                    </button>

                    <button
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-60"
                      onClick={() => openPreflight(b, true, false)}
                      disabled={pfBusy}
                      title="Install & Publish (preflight enforced)"
                    >
                      Install & Publish
                    </button>

                    <button
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-60"
                      onClick={() => openPreflight(b, true, true)}
                      disabled={pfBusy}
                      title="Reinstall: new version + repoint app (preflight enforced for publish)"
                    >
                      Reinstall
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-700">{b.description}</div>

                <div className="text-xs text-gray-500">
                  Requires providers: {b.requires_providers}
                  {installed && row ? (
                    <>
                      {" · "}flow_id: <code>{row.flow_id}</code>{" · "}version_id: <code>{row.version_id}</code>
                    </>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {installed ? (
                    <>
                      <Link className="px-3 py-2 rounded bg-black text-white text-sm" to={`/app/ingress/${encodeURIComponent(b.slug)}`} target="_blank">
                        Open Runner
                      </Link>
                      <Link className="px-3 py-2 rounded border text-sm hover:bg-gray-50" to={`/app/admin/ingress/apps`}>
                        View Apps
                      </Link>
                      {row ? (
                        <Link className="px-3 py-2 rounded border text-sm hover:bg-gray-50" to={`/app/admin/ingress/apps/${row.id}`}>
                          View Details
                        </Link>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-xs text-gray-500">Not installed in this tenant yet.</div>
                  )}
                </div>

                <details className="pt-2">
                  <summary className="cursor-pointer text-sm text-gray-700">Preview (DAG + schemas)</summary>
                  <div className="mt-2 grid grid-cols-1 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Flow spec</div>
                      <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-72">{pretty(b.flow_spec)}</pre>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Inputs schema</div>
                      <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-72">{pretty(b.inputs)}</pre>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Output rules</div>
                      <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-72">{pretty(b.output_rules)}</pre>
                    </div>
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}